import type { Palette } from '../../../_theme/palette.js';
import { DEFAULT_FONT_STACK, hexWithAlpha, shadeColor } from '../../../_theme/palette.js';

export interface SurveyTheme {
  key?: string;
  name?: string;
  colors?: {
    background?: string;
    card?: string;
    accent?: string;
    button?: string;
    text?: string;
    textMuted?: string;
  };
  font?: 'system' | 'inter' | 'nunito' | 'roboto';
  size?: 'compact' | 'default' | 'large';
  layout?: 'centered' | 'full-width';
  cornerRadius?: number;
  branding?: {
    showLogo?: boolean;
    logoUrl?: string;
    brandName?: string;
    showFooter?: boolean;
    footerText?: string;
  };
  backgroundImage?: string;
  backgroundImageFit?: 'cover' | 'contain' | 'auto';
  backgroundImagePosition?: string;
  backgroundImageOpacity?: number;
  // Author-supplied CSS injected inside the survey container. Use with care:
  // selectors are scoped by being rendered inside the iframe shell, so they
  // can't leak out to the host page, but they can override every default
  // style we ship. Power-user feature, no input sanitisation.
  customCss?: string;
}

const DEFAULT_COLORS = {
  background: '#f8fafc',
  card: '#ffffff',
  accent: '#5855ff',
  button: '#5855ff',
  text: '#0f172a',
  textMuted: '#64748b',
} as const;

const FONT_STACKS: Record<NonNullable<SurveyTheme['font']>, string> = {
  system: DEFAULT_FONT_STACK,
  inter: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  nunito: '"Nunito", -apple-system, BlinkMacSystemFont, sans-serif',
  roboto: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
};

export function resolveSurveyPalette(theme?: SurveyTheme): Palette {
  const colors = { ...DEFAULT_COLORS, ...(theme?.colors ?? {}) };
  const font = FONT_STACKS[theme?.font ?? 'system'] ?? FONT_STACKS.system;
  const radius = theme?.cornerRadius ?? 16;
  return {
    primary: colors.button,
    primarySoft: hexWithAlpha(colors.accent, 0.12),
    primaryDeep: shadeColor(colors.button, -0.18),
    background: colors.background,
    card: colors.card,
    text: colors.text,
    textMuted: colors.textMuted,
    onPrimary: '#ffffff',
    border: hexWithAlpha(colors.textMuted, 0.2),
    radius: 8,
    radiusLg: radius,
    font,
  };
}
